// eslint-disable-next-line import/named
import { FieldValues, useFormContext, UseFormRegister, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { JxtInputNumber, JxtTag, JxtTagSizeEnum } from '@jooxter/ui';
import { IBookingForm } from '@jooxter/utils';
import { useZoneSpaceAvailability } from '../../hooks/useZoneSpaceAvailability';
import { useZoneSpaceAvailabilityColor } from '../../hooks/useZoneSpaceAvailabilityColor';
import { IJxtBookingSeatsField } from './types';

/**
 * Number of physical attendees. On a zone it is a seat count shown against the remaining capacity;
 * anywhere else it is a plain head count.
 */
export const JxtBookingSeatsField = ({
  minPhysicalAttendees,
  maxPhysicalAttendees,
  resourceConfiguration,
  resourceState,
  bookingToEdit,
}: IJxtBookingSeatsField) => {
  const { t } = useTranslation();
  const { register, formState, getFieldState, getValues, setValue, control } = useFormContext<IBookingForm>();
  const watchPhysicalAttendees = useWatch({ control, name: 'physicalAttendees' });
  const { remainingSeats } = useZoneSpaceAvailability(
    resourceConfiguration,
    watchPhysicalAttendees,
    resourceState,
    bookingToEdit
  );
  const { zoneSpaceAvailabilityColor } = useZoneSpaceAvailabilityColor(remainingSeats, resourceConfiguration);
  const errorMessage = getFieldState('physicalAttendees').error?.message;

  const input = (
    <JxtInputNumber
      name="physicalAttendees"
      min={minPhysicalAttendees}
      max={maxPhysicalAttendees}
      showErrorMessages={false}
      register={register as unknown as UseFormRegister<FieldValues>}
      formState={formState}
      getFieldState={getFieldState}
      getValues={getValues}
      setValue={setValue}
    />
  );

  return (
    <div className="grid grid-cols-[min-content_auto] items-center gap-x-3">
      <i className="fas fa-fw fa-lg fa-users text-neutral-60 col-span-1 col-start-1 row-start-1" />
      <div className="flex items-center gap-3 col-start-2 row-start-1">
        {resourceConfiguration?.isZone ? (
          <>
            <span className="text-body-s text-neutral-120">{t('booking-form.physical-attendees.how-many-seats')}</span>
            <div className="flex-1">{input}</div>
          </>
        ) : (
          <>
            <span className="text-body-s text-neutral-120 capitalize">{t('for')}</span>
            <div className="w-[119px]">{input}</div>
            <span className="text-body-s text-neutral-120">
              {t('booking-form-physical-attendees-label', { physicalAttendeesValue: watchPhysicalAttendees || 0 })}
            </span>
          </>
        )}
      </div>
      {errorMessage && typeof errorMessage === 'string' ? (
        <p className="text-red-100 pt-1 col-start-2 row-start-2">{errorMessage}</p>
      ) : (
        resourceConfiguration?.isZone && (
          <div className="pt-2 col-start-2 row-start-2">
            <JxtTag
              text={t<string>('zone-availability-booking-slider', { remainingSeats })}
              color={zoneSpaceAvailabilityColor}
              size={JxtTagSizeEnum.SMALL}
            />
          </div>
        )
      )}
    </div>
  );
};

export default JxtBookingSeatsField;
